import { useEffect, useMemo, useRef, useState } from 'react';
import { MapChart } from 'echarts/charts';
import { TooltipComponent, VisualMapContinuousComponent } from 'echarts/components';
import { init, registerMap, use, type EChartsCoreOption, type EChartsType, type ECElementEvent } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { formatCountryName } from './GeoUi';
import type { CountryGeography, GeographyMetrics } from './types';
import { WorldMapNavigationControls } from './WorldMapNavigationControls';
import { createWorldMapSvg } from './WorldMapSource';
import { WORLD_MAP_MIN_ZOOM, WorldMapViewportController, type MapPanDirection, type WorldMapCamera } from './WorldMapViewportController';

export type GeographyMetricKey = keyof GeographyMetrics;
export type GeographyScale = 'linear' | 'logarithmic';

const mapName = 'voogzy-world';
const mapSeriesId = 'world-map-series';

use([MapChart, TooltipComponent, VisualMapContinuousComponent, CanvasRenderer]);
registerMap(mapName, { svg: createWorldMapSvg() });

interface MapDatum {
  name: string;
  value: number;
  rawValue: number;
  selected: boolean;
}

export function WorldMapChart(props: {
  countries: CountryGeography[];
  metric: GeographyMetricKey;
  scale: GeographyScale;
  selectedCountryCode: string | null;
  onSelectCountry: (countryCode: string) => void;
}): React.JSX.Element {
  const container = useRef<HTMLDivElement>(null);
  const chart = useRef<EChartsType | null>(null);
  const countrySelectionHandler = useRef(props.onSelectCountry);
  const viewportController = useRef<WorldMapViewportController | null>(null);
  if (viewportController.current === null) viewportController.current = new WorldMapViewportController();
  const viewport = viewportController.current;
  const [camera, setCamera] = useState<WorldMapCamera>(viewport.getCamera());
  const option = useMemo(() => createMapOption(props.countries, props.metric, props.scale, props.selectedCountryCode), [props.countries, props.metric, props.scale, props.selectedCountryCode]);
  countrySelectionHandler.current = props.onSelectCountry;

  useEffect(() => {
    const element = container.current;
    if (element === null) return undefined;
    const instance = init(element, undefined, { renderer: 'canvas' });
    chart.current = instance;

    function selectCountry(parameters: ECElementEvent): void {
      if (typeof parameters.name === 'string' && /^[A-Z]{2}$/.test(parameters.name)) {
        countrySelectionHandler.current(parameters.name);
      }
    }

    function constrainNativeRoam(): void {
      const acceptedCamera = viewport.accept(...readChartCamera(instance));
      applyCamera(instance, acceptedCamera);
      setCamera(acceptedCamera);
    }

    function resize(): void {
      instance.resize();
      applyCamera(instance, viewport.getCamera());
    }

    instance.on('click', selectCountry);
    instance.on('georoam', constrainNativeRoam);
    window.addEventListener('resize', resize);
    return () => {
      instance.off('click', selectCountry);
      instance.off('georoam', constrainNativeRoam);
      window.removeEventListener('resize', resize);
      instance.dispose();
      chart.current = null;
    };
  }, [viewport]);

  useEffect(() => {
    const instance = chart.current;
    if (instance === null) return;
    instance.setOption(option);
    applyCamera(instance, viewport.getCamera());
  }, [option, viewport]);

  function applyControlledCamera(nextCamera: WorldMapCamera): void {
    const instance = chart.current;
    if (instance !== null) applyCamera(instance, nextCamera);
    setCamera(nextCamera);
  }

  function zoomIn(): void {
    applyControlledCamera(viewport.zoomIn());
  }

  function zoomOut(): void {
    applyControlledCamera(viewport.zoomOut());
  }

  function reset(): void {
    applyControlledCamera(viewport.reset());
  }

  function panNorth(): void {
    applyControlledCamera(viewport.pan('north'));
  }

  function panEast(): void {
    applyControlledCamera(viewport.pan('east'));
  }

  function panSouth(): void {
    applyControlledCamera(viewport.pan('south'));
  }

  function panWest(): void {
    applyControlledCamera(viewport.pan('west'));
  }

  return (
    <div className="world-map-stage">
      <div ref={container} className="world-map-chart" />
      <WorldMapNavigationControls
        camera={camera}
        canZoomIn={viewport.canZoomIn()}
        canZoomOut={viewport.canZoomOut()}
        canPanNorth={viewport.canPan('north')}
        canPanEast={viewport.canPan('east')}
        canPanSouth={viewport.canPan('south')}
        canPanWest={viewport.canPan('west')}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onReset={reset}
        onPanNorth={panNorth}
        onPanEast={panEast}
        onPanSouth={panSouth}
        onPanWest={panWest}
      />
    </div>
  );
}

function createMapOption(
  countries: CountryGeography[],
  metric: GeographyMetricKey,
  scale: GeographyScale,
  selectedCountryCode: string | null,
): EChartsCoreOption {
  const data: MapDatum[] = countries.map((country) => {
    const rawValue = country[metric];
    return {
      name: country.countryCode,
      value: scale === 'logarithmic' ? Math.log10(rawValue + 1) : rawValue,
      rawValue,
      selected: country.countryCode === selectedCountryCode,
    };
  });
  const maximum = Math.max(1, ...data.map(readMapValue));
  return {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'item', formatter: formatMapTooltip },
    visualMap: {
      min: 0,
      max: maximum,
      left: 8,
      bottom: 6,
      calculable: true,
      text: [scale === 'logarithmic' ? 'Higher · log' : 'Higher', 'None'],
      textStyle: { color: '#718097', fontSize: 10 },
      inRange: { color: ['#182235', '#294b87', '#5c83e9', '#a9c1ff'] },
      outOfRange: { color: '#151d29' },
    },
    series: [{
      id: mapSeriesId,
      type: 'map',
      map: mapName,
      center: [500, 250],
      zoom: WORLD_MAP_MIN_ZOOM,
      roam: 'scale',
      scaleLimit: { min: 1, max: 12 },
      selectedMode: 'single',
      emphasis: { label: { show: false }, itemStyle: { areaColor: '#c6d5ff', borderColor: '#f2f6ff' } },
      select: { label: { show: false }, itemStyle: { areaColor: '#e6b86f', borderColor: '#ffe0a5', borderWidth: 1.5 } },
      itemStyle: { areaColor: '#151d29', borderColor: '#2d394c', borderWidth: 0.55 },
      data,
    }],
  };
}

function readChartCamera(chart: EChartsType): [readonly number[] | null, number | null] {
  const option = chart.getOption() as { series?: Array<{ id?: unknown; center?: unknown; zoom?: unknown }> };
  const series = option.series?.find(isWorldMapSeries);
  const center = Array.isArray(series?.center) ? series.center.filter(isNumber) : null;
  const zoom = typeof series?.zoom === 'number' ? series.zoom : null;
  return [center, zoom];
}

function isWorldMapSeries(series: { id?: unknown }): boolean {
  return series.id === mapSeriesId;
}

function isNumber(value: unknown): value is number {
  return typeof value === 'number';
}

function applyCamera(chart: EChartsType, camera: WorldMapCamera): void {
  chart.setOption({
    series: [{
      id: mapSeriesId,
      center: camera.center,
      zoom: camera.zoom,
      roam: camera.zoom > WORLD_MAP_MIN_ZOOM ? true : 'scale',
    }],
  });
}

function formatMapTooltip(parameters: { data?: unknown; name: string }): string {
  const datum = parameters.data as MapDatum | undefined;
  const value = datum?.rawValue ?? 0;
  const countryCode = parameters.name;
  const countryName = /^[A-Z]{2}$/.test(countryCode) ? formatCountryName(countryCode) : countryCode;
  return `${countryName}<br/><strong>${new Intl.NumberFormat().format(value)}</strong>`;
}

function readMapValue(value: MapDatum): number {
  return value.value;
}
